import React from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { cases } from "../../data/cases/cases";

const CaseDetail: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();

  // Find the case by name (URL-friendly version)
  const caseItem = cases.find(
    (c) =>
      c.name.toLowerCase().replace(/\s+/g, "-") === name
  );

  if (!caseItem) {
    return (
      <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Case not found
          </h1>
          <button
            onClick={() => navigate("/cases")}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Cases
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${caseItem.name} - VEA Group Case Study`}</title>
        <meta
          name="description"
          content={caseItem.description}
        />
        <meta
          name="keywords"
          content={`${caseItem.name}, case study, VEA Group, ${caseItem.year}, digital transformation`}
        />
        <link
          rel="canonical"
          href={`https://veagroup.global/cases/${name}`}
        />
        <link rel="icon" href="/favicon.ico" />
        <link rel="manifest" href="/manifest.json" />
        {/* Open Graph */}
        <meta
          property="og:title"
          content={`${caseItem.name} - VEA Group Case Study`}
        />
        <meta
          property="og:description"
          content={caseItem.description}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://veagroup.global/cases/${name}`}
        />
        <meta
          property="og:image"
          content={caseItem.image}
        />
        {/* Twitter Card */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content={`${caseItem.name} - VEA Group Case Study`}
        />
        <meta
          name="twitter:description"
          content={caseItem.description}
        />
        <meta
          name="twitter:image"
          content={caseItem.image}
        />
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-black transition-colors duration-500">
        {/* Header Section */}
        <section className="container mx-auto px-16 pt-32">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="h-px bg-gray-300 dark:bg-gray-700"></div>
              {/* Image and Title Row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex flex-col lg:flex-row items-center gap-8 "
              >
                {/* Featured Image */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="flex-shrink-0"
                >
                  <div className="relative w-32 h-20 lg:w-48 lg:h-32 rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={caseItem.image}
                      alt={caseItem.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </motion.div>

                {/* Project Title */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="text-center"
                >
                  <motion.h1
                    className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white leading-tight"
                    style={{
                      fontFamily: "Merriweather, serif",
                    }}
                  >
                    {caseItem.name}
                  </motion.h1>
                </motion.div>
              </motion.div>

              {/* Project Details Grid */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-2 lg:grid-cols-4 gap-6"
              >
                {/* Service */}
                <div className="space-y-2">
                  <div className="h-px bg-gray-300 dark:bg-gray-700"></div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                      Service
                    </p>
                    <p className="text-base text-gray-900 dark:text-white font-semibold">
                      {caseItem.service}
                    </p>
                  </div>
                </div>

                {/* Client */}
                <div className="space-y-2">
                  <div className="h-px bg-gray-300 dark:bg-gray-700"></div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                      Client
                    </p>
                    <p className="text-base text-gray-900 dark:text-white font-semibold">
                      {caseItem.client}
                    </p>
                  </div>
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <div className="h-px bg-gray-300 dark:bg-gray-700"></div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                      Date
                    </p>
                    <p className="text-base text-gray-900 dark:text-white font-semibold">
                      {caseItem.date}
                    </p>
                  </div>
                </div>

                {/* Technology */}
                <div className="space-y-2">
                  <div className="h-px bg-gray-300 dark:bg-gray-700"></div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                      Technology
                    </p>
                    <p className="text-base text-gray-900 dark:text-white font-semibold">
                      {caseItem.technology}
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CaseDetail;
